import { animated, to, useTransition } from '@react-spring/web';
import { ChangeEvent, ComponentPropsWithoutRef, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { theme } from '../../common/ui/theme';
import { Text } from '../Text/Text';

import { CheckboxLabel } from './components/CheckboxLabel';
import { CheckmarkBackground } from './components/CheckmarkBackground';
import { CheckmarkIcon } from './components/CheckmarkIcon';
import { HiddenInput } from './components/HiddenCheckboxInput';
import { LabelWrapper } from './components/LabelWrapper';

const ComponentWrapper = styled.div`
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    grid-column-gap: ${theme.space[8]};
    align-items: center;
    min-width: 0;
    row-gap: ${theme.space[4]};
`;

export interface CheckboxProps extends ComponentPropsWithoutRef<'input'> {
    label?: ReactNode;
    description?: ReactNode;
    indeterminate?: boolean;
    truncateLabel?: boolean;
}

const getLabelTitle = (label: ReactNode) => {
    if (typeof label === 'string') {
        return label;
    }

    return undefined;
};

export const Checkbox = ({
    label,
    description,
    indeterminate,
    truncateLabel = false,
    ...inputProps
}: CheckboxProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    // Formik compatability: props.value can have a boolean value
    const valueAsChecked = typeof inputProps.value === 'boolean' ? inputProps.value : undefined;
    const { checked, defaultChecked, onChange, ...restInputProps } = inputProps;

    const [isChecked, setChecked] = useState<boolean | undefined>(checked ?? valueAsChecked ?? defaultChecked);

    useEffect(() => {
        if (checked !== undefined || valueAsChecked !== undefined) {
            setChecked(checked ?? valueAsChecked);
        }
    }, [checked, valueAsChecked]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = !!indeterminate;
        }
    }, [indeterminate]);

    const checkmarkTransition = useTransition(isChecked && !indeterminate, {
        from: {
            strokeOffset: 34,
            scale: 1,
            translateX: '3px',
            opacity: 1,
        },
        enter: {
            strokeOffset: 19,
            scale: 1,
            translateX: '0px',
            opacity: 1,
        },
        leave: {
            strokeOffset: 19,
            scale: 0.5,
            translateX: '0px',
            opacity: 0,
        },
        config: {
            duration: 150,
        },
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event);
        setChecked(event.target.checked);
    };

    const handleLabelMouseDown = (event: MouseEvent<HTMLLabelElement>) => {
        if (inputRef.current?.matches(':focus-visible')) {
            event.preventDefault();
        }
    };

    return (
        <ComponentWrapper>
            <LabelWrapper disabled={inputProps.disabled} onMouseDown={handleLabelMouseDown}>
                <HiddenInput
                    {...restInputProps}
                    ref={inputRef}
                    type="checkbox"
                    onChange={handleChange}
                    checked={!!isChecked}
                />
                <CheckmarkIcon>
                    <CheckmarkBackground
                        checked={isChecked}
                        disabled={inputProps.disabled}
                        $indeterminate={indeterminate}
                    >
                        {indeterminate ? (
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                stroke={
                                    inputProps.disabled
                                        ? theme.colors.foreground.disabled
                                        : theme.colors.foreground.onEmphasis
                                }
                                strokeWidth={1.5}
                                strokeLinecap="round"
                            >
                                <path d="M3 6H9" />
                            </svg>
                        ) : null}
                        {checkmarkTransition((style, isCheckmarkVisible) =>
                            isCheckmarkVisible ? (
                                <animated.svg
                                    style={{
                                        transform: to(
                                            [style.scale, style.translateX],
                                            (s, t) => `scale(${s}) translateX(${t})`
                                        ),
                                        opacity: style.opacity,
                                        position: 'absolute',
                                        top: '3px',
                                    }}
                                    width="16"
                                    height="14"
                                    viewBox="0 0 16 14"
                                    fill="none"
                                    stroke={
                                        inputProps.disabled
                                            ? theme.colors.foreground.disabled
                                            : theme.colors.foreground.onEmphasis
                                    }
                                    strokeWidth={1.5}
                                    strokeDasharray={30}
                                    strokeDashoffset={style.strokeOffset}
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                >
                                    <path d="M4 6L6.5 8.5L12 3" />
                                </animated.svg>
                            ) : null
                        )}
                    </CheckmarkBackground>
                </CheckmarkIcon>
                {label ? (
                    <CheckboxLabel
                        disabled={inputProps.disabled}
                        title={truncateLabel ? getLabelTitle(label) : undefined}
                        $truncateLabel={truncateLabel}
                    >
                        {label}
                    </CheckboxLabel>
                ) : null}
            </LabelWrapper>

            {description ? (
                <Text
                    variant="extraSmall"
                    color={inputProps.disabled ? 'foreground.disabled' : 'foreground.subtle'}
                    style={{ gridColumnStart: 2 }}
                >
                    {description}
                </Text>
            ) : null}
        </ComponentWrapper>
    );
};
